import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Check, Loader2, Plus, Trash2, Upload } from "lucide-react";
import * as adminApi from "@/api/admin";
import type { NovaDeal } from "@/api/admin";
import RichTextEditor from "@/components/RichTextEditor";
import { ImageDropzone } from "./ImageDropzone";

// Slug of the PageImage row holding the Nova page hero image
const NOVA_HEADER_SLUG = "nova-header";
const NOVA_HEADLINE_SLUG = "nova-hero";
const DEFAULT_HEADLINE = "Με τη Nova,\nκερδίζεις PADU!";

function htmlToPlainText(html: string) {
  if (typeof window === "undefined") return html.replace(/<[^>]+>/g, " ");
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.replace(/\s+/g, " ").trim() || "";
}

export function NovaHeaderView() {
  const [headline, setHeadline] = useState(DEFAULT_HEADLINE);
  const [origHeadline, setOrigHeadline] = useState(DEFAULT_HEADLINE);
  const [headlineHtml, setHeadlineHtml] = useState(DEFAULT_HEADLINE);
  const [origHeadlineHtml, setOrigHeadlineHtml] = useState(DEFAULT_HEADLINE);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingText, setSavingText] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.getPageContent(NOVA_HEADLINE_SLUG),
      adminApi.getPageImage(NOVA_HEADER_SLUG),
    ])
      .then(([content, imageUrl]) => {
        const nextHeadline = content.title || DEFAULT_HEADLINE;
        const nextHeadlineHtml = content.content || nextHeadline;
        setHeadline(nextHeadline);
        setOrigHeadline(nextHeadline);
        setHeadlineHtml(nextHeadlineHtml);
        setOrigHeadlineHtml(nextHeadlineHtml);
        setCurrentUrl(imageUrl);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSaveText = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSavingText(true);
    try {
      await adminApi.updatePageContent(NOVA_HEADLINE_SLUG, {
        title: htmlToPlainText(headlineHtml) || headline,
        content: headlineHtml,
      });
      setOrigHeadline(headline);
      setOrigHeadlineHtml(headlineHtml);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaveError("Failed to save the headline.");
    } finally {
      setSavingText(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setSaveError(null);
    setSaving(true);
    try {
      await adminApi.uploadPageImage(NOVA_HEADER_SLUG, file);
      setFile(null);
      load();
    } catch {
      setSaveError("Failed to upload header image.");
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load header image.</AlertDescription>
      </Alert>
    );
  }
  if (loading) return <Skeleton className="h-72 rounded-lg" />;

  return (
    <div className="space-y-6">
      {saveError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Headline</h2>
        <form onSubmit={handleSaveText} className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-gray-500">
              Main headline shown in the Nova hero section. Use the toolbar for rich text formatting.
            </label>
            <RichTextEditor
              value={headlineHtml}
              onChange={(html) => {
                setHeadlineHtml(html);
                setHeadline(htmlToPlainText(html));
              }}
              minHeight="180px"
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={savingText || headlineHtml === origHeadlineHtml}
              className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white"
            >
              {savingText ? (
                <Loader2 size={14} className="animate-spin" />
              ) : saved ? (
                <><Check size={14} className="mr-1" /> Saved</>
              ) : (
                "Save headline"
              )}
            </Button>
          </div>
        </form>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Current header image</h2>
        {currentUrl ? (
          <div className="max-w-sm mx-auto rounded-md overflow-hidden bg-gray-100 border border-gray-200">
            <img src={currentUrl} alt="Nova header" className="w-full h-auto object-cover" />
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-8 text-center">
            No header image uploaded — the Nova page shows no hero image.
          </p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Upload new header image</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <ImageDropzone file={file} onChange={setFile} />
          <div className="flex justify-end">
            <Button type="submit" disabled={saving || !file} className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} className="mr-1" /> Save</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function NovaDealsView() {
  const [deals, setDeals] = useState<NovaDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    adminApi.getNovaDeal()
      .then(setDeals)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !imageFile) return;
    setAdding(true);
    await adminApi.createNovaDeal({ name: name.trim(), image: imageFile, order: deals.length });
    setName("");
    setImageFile(null);
    setAdding(false);
    load();
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    await adminApi.deleteNovaDeal(id);
    setDeletingId(null);
    setConfirmDeleteId(null);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Add Nova Deal Partner</h2>
        <form onSubmit={handleAdd} className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-gray-500">Partner name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Nova" required />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-500">Deal image (flyer / banner)</label>
            <ImageDropzone file={imageFile} onChange={setImageFile} />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={adding || !name.trim() || !imageFile} className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white">
              {adding ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Add Partner</>}
            </Button>
          </div>
        </form>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load Nova deals.</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-52 rounded-lg" />)}
        </div>
      ) : deals.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">No Nova deal partners yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {deals.map(deal => (
            <div
              key={deal.id}
              className="border border-gray-200 rounded-lg p-3 flex flex-col items-center gap-2 group relative bg-white overflow-hidden"
            >
              <div className="w-full h-40 rounded-md overflow-hidden bg-gray-100">
                <img src={deal.image_url} alt={deal.name} className="w-full h-full object-cover" />
              </div>
              <span className="text-xs text-gray-600 font-medium text-center">{deal.name}</span>
              <span className="text-[9px] font-bold text-[#00d9d9] uppercase tracking-widest bg-[#00d9d9]/10 border border-[#00d9d9]/30 rounded-full px-2.5 py-1">
                Nova Deal
              </span>
              <button
                onClick={() => setConfirmDeleteId(deal.id)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                aria-label={`Delete ${deal.name}`}
              >
                <Trash2 size={14} />
              </button>
              {confirmDeleteId === deal.id && (
                <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-3 text-center">
                  <p className="text-xs text-gray-700">
                    Delete <span className="font-semibold">{deal.name}</span>?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={deletingId === deal.id}
                      className="px-3 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(deal.id)}
                      disabled={deletingId === deal.id}
                      className="px-3 py-1 text-xs rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 flex items-center gap-1"
                    >
                      {deletingId === deal.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
